import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchModels } from '../api';
import { ProviderPill } from '../components/EndpointBadge';
import { getEffectivePricing, usePricingVersion } from '../pricingOverrides';
import type { ApiEndpointModel } from '../types';

/** Slug used in the SPA route for an endpoint (derived from its backend path). */
function slugForModel(model: ApiEndpointModel): string {
  return model.path.replace(/^\/v1\//, '');
}

function pricingSummary(pricing: Record<string, number>): string {
  if (pricing.per_call != null) return `$${pricing.per_call.toFixed(3)} / call`;
  const parts: string[] = [];
  if (pricing.input_per_m != null) parts.push(`$${pricing.input_per_m}/1M in`);
  if (pricing.output_per_m != null) parts.push(`$${pricing.output_per_m}/1M out`);
  return parts.join(' · ') || '—';
}

export function EndpointsPage() {
  const [models, setModels] = useState<ApiEndpointModel[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Re-render the pricing summaries when a per-endpoint override changes.
  usePricingVersion();

  useEffect(() => {
    let cancelled = false;
    fetchModels()
      .then((m) => {
        if (!cancelled) setModels(m);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">API Endpoints</h1>
        <p className="text-sm text-slate-500 mt-1">
          Wrapped image-generation endpoints. Every call is routed, recorded (prompt, cost, inputs,
          outputs) and its images stored, so all usage is tracked under one umbrella. Pick an
          endpoint to test it manually.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm mb-4">
          Failed to load endpoints: {error}
        </div>
      )}

      {!models && !error && <div className="text-sm text-slate-500">Loading endpoints…</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {models?.map((model) => {
          const placeholder = model.model_id?.startsWith('PLACEHOLDER_');
          return (
            <Link
              key={model.key}
              to={`/gen-api/${slugForModel(model)}`}
              className="block bg-white rounded-xl border border-slate-200 shadow-soft p-5 hover:shadow-card hover:border-indigo-300 transition-all"
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-slate-900">{model.label}</h2>
                <ProviderPill
                  provider={model.active_provider || model.provider}
                  configuredProvider={model.provider}
                />
              </div>
              <p className="text-sm text-slate-500 mt-1.5 line-clamp-3">{model.description}</p>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                <span className="font-mono truncate">{model.model_id || model.endpoint}</span>
                <span className="shrink-0 ml-2">{pricingSummary(getEffectivePricing(model))}</span>
              </div>
              {placeholder && (
                <div className="mt-2 text-[11px] text-warning bg-warning/10 rounded px-2 py-1">
                  Model id is a placeholder — set it in models.yaml.
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
